import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Trash2, UserPlus } from "lucide-react";
import { api } from "@/data/api";
import { npmApiErrorMessage } from "@/data/npmState";
import { useAsync } from "@/hooks/useAsync";
import { AsyncBoundary, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

const ROLE = { project_admin: "Partner / Project Admin", project_viewer: "Project Viewer" };
const input = "rounded border border-line bg-background px-2 py-1.5 text-sm";

// Acesso INTERNO (staff/sócios) a este Project — distinto do acesso de
// Client Portal, que vem da membership do Client.
export function ProjectAccess({ projectId, client }) {
  const q = useAsync(() => api.getProjectMembers(projectId), [projectId]);
  const [form, setForm] = useState({ email: "", role: "project_viewer" });
  const [result, setResult] = useState(null);
  const run = async (fn, text) => {
    setResult(null);
    try {
      const r = await fn();
      setResult({ ok: true, text: text(r), link: r?.link });
      q.reload();
    } catch (e) {
      setResult({ ok: false, text: npmApiErrorMessage(e) });
    }
  };
  const add = (event) => {
    event.preventDefault();
    run(() => api.addProjectMember(projectId, form), (r) => (r.created ? `Convite interno criado para ${r.email}.` : `Acesso concedido a ${r.email}.`));
    setForm({ email: "", role: "project_viewer" });
  };
  return <div className="grid gap-5 lg:grid-cols-2">
    <Panel title="Internal Access" icon={ShieldCheck} bodyClassName="space-y-3 text-sm">
      <p className="text-xs text-muted-foreground">Funcionários, sócios e operadores com acesso somente a este Project (escopo PROJECT). Não é acesso de cliente.</p>
      <AsyncBoundary loading={q.loading} error={q.error}>
        {q.data && <>
          {q.data.internal_members.length ? <ul className="space-y-1" data-testid="project-internal-members">{q.data.internal_members.map((m) => <li key={m.user_id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2">
            <span><span className="font-medium text-foreground">{m.name}</span> <span className="text-xs text-muted-foreground">{m.email} · {m.roles.map((r) => ROLE[r] || r).join(", ")}</span></span>
            <span className="flex items-center gap-2"><StatusBadge status={m.status === "invited" ? "pending" : m.status} size="sm" pulse={false} />
              <Button size="sm" variant="ghost" aria-label={`Remover ${m.email}`} onClick={() => run(() => api.removeProjectMember(projectId, m.user_id), () => `Acesso de ${m.email} removido.`)}><Trash2 className="h-3.5 w-3.5" /></Button></span>
          </li>)}</ul> : <p className="text-muted-foreground">Nenhum membro interno com escopo neste Project.</p>}
          <p className="text-xs text-muted-foreground">Platform Admins (GLOBAL) com acesso a tudo: {q.data.platform_admins}.</p>
        </>}
      </AsyncBoundary>
      <form onSubmit={add} className="grid gap-2 sm:grid-cols-3" data-testid="project-member-form">
        <input className={input} type="email" required placeholder="e-mail interno" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <select className={input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="project_viewer">Project Viewer</option><option value="project_admin">Partner / Project Admin</option>
        </select>
        <Button type="submit" size="sm"><UserPlus className="h-3.5 w-3.5" /> Dar acesso</Button>
      </form>
      {result && <p role="alert" className={`rounded-lg border px-3 py-2 ${result.ok ? "border-ok/30 text-ok" : "border-err/30 text-err"}`}>{result.text}{result.link && <span className="mt-1 block break-all font-mono text-xs text-foreground">Link (24h): {result.link}</span>}</p>}
    </Panel>
    <Panel title="Client Portal Access" icon={Users} bodyClassName="space-y-2 text-sm">
      {client.internal
        ? <p className="text-muted-foreground">Project interno: nunca aparece no Portal do Cliente.</p>
        : <>
          <p>Visível no Portal para os membros explícitos do Client <Link className="text-brand underline" to={`/clientes/${client.id}`}>{client.name}</Link>{q.data ? ` (${q.data.client_portal_members})` : ""}.</p>
          <p className="text-xs text-muted-foreground">O e-mail de contato do Client não concede acesso. Gerencie os membros na página do Client.</p>
        </>}
      {q.data?.portal_project_grants?.length > 0 && <p className="text-xs text-warn">Concessões de Portal com escopo neste Project: {q.data.portal_project_grants.map((m) => m.email).join(", ")}</p>}
    </Panel>
  </div>;
}

export function ProjectOwnership({ projectId, client, onChanged }) {
  const clients = useAsync(() => api.listClientsRaw(), []);
  const [target, setTarget] = useState(client.id);
  const [message, setMessage] = useState(null);
  const save = async () => {
    setMessage(null);
    try {
      await api.transferProjectClient(projectId, target);
      setMessage({ ok: true, text: "Client do Project atualizado (auditado). Nenhum acesso foi concedido." });
      onChanged();
    } catch (e) {
      setMessage({ ok: false, text: npmApiErrorMessage(e) });
    }
  };
  return <Panel title="Ownership" icon={ShieldCheck} bodyClassName="space-y-2 text-sm">
    <div className="flex flex-wrap items-center gap-2">
      <select value={target} onChange={(e) => setTarget(e.target.value)} className={input} data-testid="project-client-select">
        {(clients.data || []).map((c) => <option key={c.id} value={c.id}>{`${c.name}${c.is_internal ? " (Interno)" : ""}`}</option>)}
      </select>
      <Button size="sm" variant="outline" disabled={target === client.id} onClick={save} data-testid="project-client-save">Alterar Client</Button>
    </div>
    <p className="text-xs text-muted-foreground">Atribuir o Project a um Client não concede acesso a ninguém; o Portal depende de membership explícita.</p>
    {message && <p role="alert" className={message.ok ? "text-ok" : "text-err"}>{message.text}</p>}
  </Panel>;
}
